/**
 * Issued statements, for a creator or for a fan.
 *
 * The dashboard above this shows a balance that moves. This shows the months
 * that have stopped moving — the only thing in the product either of them can
 * hand to an accountant, and the only figure here that will read the same next
 * March as it does today.
 *
 * Two details are deliberate and easy to undo by accident:
 *
 *   The download is a plain link, not a fetch-and-blob. The API is same-origin
 *   and the session is a cookie, so the browser's own download machinery
 *   handles it — including the filename, which the server sets. Fetching it
 *   into memory to make an object URL would produce a worse filename and a
 *   file the browser never saw a `Content-Disposition` for.
 *
 *   The document is never parsed and re-rendered into this table. The rows come
 *   from the summary the list endpoint returns. A statement's authority is its
 *   bytes, and a UI that redrew them from parsed JSON would be showing
 *   something subtly other than the file it is offering.
 *
 * The wording differs by kind and the arithmetic does not. A fan's cashback is
 * a rebate on their own spending rather than income, and calling it "earned" on
 * a document somebody might hand to an accountant would be describing money as
 * something it is not — the same reasoning as `kinds.ts` on the API side.
 */
import { useStatements } from '../api/hooks.js';
import { statementSurface, type StatementKind, type StatementWire } from '../api/client.js';
import { ErrorNote, Panel } from './shared.js';
import { Money } from './Money.js';

/** True of both, and the sentence a reader most needs to see. */
const SUBTITLE =
  'Issued after a month closes. Each one is fixed once issued — corrections appear in the month they were made.';

interface Wording {
  readonly empty: string;
  readonly earned: string;
  readonly paidOut: string;
}

const WORDING: Readonly<Record<StatementKind, Wording>> = {
  creator: {
    empty:
      'Nothing yet. Your first statement is issued after the end of the first month you earn in.',
    earned: 'Earned',
    paidOut: 'Paid out',
  },
  member: {
    empty:
      'Nothing yet. Your first statement is issued after the end of the first month you earn cashback in.',
    earned: 'Cashback',
    paidOut: 'Redeemed',
  },
};

export function StatementsPanel({ kind, enabled }: { kind: StatementKind; enabled: boolean }) {
  const statements = useStatements(kind, enabled);
  const rows = statements.data ?? [];
  const words = WORDING[kind];

  return (
    <Panel title="Statements" subtitle={SUBTITLE}>
      {statements.isError && <ErrorNote error={statements.error} />}
      {statements.isPending && <p className="empty">Loading…</p>}

      {!statements.isPending && rows.length === 0 && <p className="empty">{words.empty}</p>}

      {rows.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Period</th>
                <th scope="col">{words.earned}</th>
                <th scope="col">{words.paidOut}</th>
                <th scope="col">Closing balance</th>
                <th scope="col">
                  <span className="visually-hidden">Download</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((statement) => (
                <tr key={statement.id}>
                  <th scope="row">
                    {monthName(statement.period)}
                    <span className="table__note">{covers(statement)}</span>
                  </th>
                  <td>
                    <Money value={statement.earned} />
                  </td>
                  <td>
                    <Money value={statement.paidOut} />
                  </td>
                  <td>
                    <Money value={statement.closing} />
                  </td>
                  <td className="table__right">
                    <a
                      className="button button--quiet"
                      href={`/v1/${statementSurface(kind)}/me/statements/${statement.period}/download`}
                      /**
                       * The server names the file. `download` without a value asks
                       * the browser to save rather than navigate, and leaves
                       * `Content-Disposition` to supply the name — passing one here
                       * would override the server's and drift from it.
                       */
                      download
                    >
                      Download CSV
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/** `2026-07` as `July 2026`, without pulling in a date library. */
function monthName(period: string): string {
  const [year, month] = period.split('-');
  if (year === undefined || month === undefined) return period;
  const at = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
  return at.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/**
 * What the statement actually covers.
 *
 * A first statement covers everything from the beginning, which is usually more
 * than the month in its title, and saying "July" alone would be wrong in the
 * one place a reader is most likely to check the arithmetic.
 */
function covers(statement: StatementWire): string {
  const lines = `${statement.lineCount} ${statement.lineCount === 1 ? 'entry' : 'entries'}`;
  return statement.coveredFrom === null ? `${lines}, from the beginning` : lines;
}
